import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Twitter, Send, Zap, ZapOff, CheckCircle2, AlertTriangle } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { useToast } from "../../hooks/use-toast";

const DEFAULT_TEMPLATE = "📰 {title}\n\n{url}";

const TwitterPanel = ({ token }) => {
  const a = useMemo(() => adminApi(token), [token]);
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tmpl, setTmpl] = useState(DEFAULT_TEMPLATE);
  const [autoPost, setAutoPost] = useState(true);
  const [testText, setTestText] = useState("🧪 Habthek Test-Tweet – funktioniert die Anbindung? #Habbo");

  const _errMsg = (e) => {
    const d = e?.response?.data?.detail ?? e?.response?.data ?? e?.message ?? e;
    if (typeof d === "string") return d;
    try { return JSON.stringify(d); } catch (_) { return String(d); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const s = await a.twitterStatus();
      setStatus(s);
      setTmpl(s?.settings?.template || DEFAULT_TEMPLATE);
      setAutoPost(s?.settings?.auto_post ?? true);
    } catch (e) {
      toast({ title: "Laden fehlgeschlagen", description: _errMsg(e), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onSaveSettings = async (ev) => {
    if (ev?.preventDefault) ev.preventDefault();
    setBusy(true);
    try {
      await a.twitterUpdateSettings({ template: tmpl, auto_post: autoPost });
      toast({ title: "Einstellungen gespeichert" });
      await load();
    } catch (e) { toast({ title: "Fehler", description: _errMsg(e), variant: "destructive" }); }
    finally { setBusy(false); }
  };

  const onTestTweet = async (ev) => {
    if (ev?.preventDefault) ev.preventDefault();
    setBusy(true);
    try {
      const r = await a.twitterTestTweet(testText);
      toast({ title: "Tweet gesendet", description: r?.tweet_id ? `ID: ${r.tweet_id}` : "OK" });
      await load();
    } catch (e) {
      toast({ title: "Tweet fehlgeschlagen", description: _errMsg(e), variant: "destructive" });
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  const allKeysPresent = status?.consumer_key_present && status?.consumer_secret_present
    && status?.access_token_present && status?.access_token_secret_present;

  return (
    <div className="space-y-4" data-testid="twitter-panel">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Twitter className="h-5 w-5 text-[#1d9bf0]" />
            Twitter / X – Auto-Post
          </h2>
          <p className="mt-1 text-sm text-zinc-400">
            Habthek nutzt einen vorkonfigurierten Twitter-Account (Credentials liegen in <code className="bg-white/5 px-1 rounded">backend/.env</code>).
            Neue News-Artikel werden bei &bdquo;Online&ldquo; automatisch getwittert. RSS-Backup: <code className="bg-white/5 px-1 rounded">/api/rss</code>.
          </p>
        </div>
      </div>

      {/* Connection Card */}
      {status?.connected && status?.account ? (
        <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4" data-testid="twitter-connected">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-300" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-white">
                Verbunden als @{status.account.screen_name}
                {status.account.name ? <span className="ml-2 text-xs font-normal text-zinc-400">({status.account.name})</span> : null}
              </div>
              <div className="text-[11px] text-zinc-500">OAuth 1.0a (pre-authorized) – Access Token in .env</div>
            </div>
          </div>
        </div>
      ) : status?.connected ? (
        <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 p-4" data-testid="twitter-warning">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-300 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-white">Credentials gesetzt, aber verify_credentials schlug fehl</div>
              <div className="text-[11px] text-zinc-400 mt-1">
                {status.warning || "Unbekannter Fehler beim Verifizieren der Tokens."}
              </div>
              <div className="text-[11px] text-zinc-500 mt-2">
                Bitte pruefe im Twitter Developer Portal (Keys and tokens): sind Consumer Key/Secret + Access Token/Secret aktuell?
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-rose-500/25 bg-rose-500/5 p-4" data-testid="twitter-disconnected">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-rose-300 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-white">
                {allKeysPresent ? "Nicht verbunden" : "Credentials unvollstaendig"}
              </div>
              <div className="text-[11px] text-zinc-400 mt-1">
                {status?.error || "Credentials in .env pruefen: TWITTER_CONSUMER_KEY, TWITTER_CONSUMER_SECRET, TWITTER_ACCESS_TOKEN, TWITTER_ACCESS_TOKEN_SECRET"}
              </div>
              <div className="mt-2 grid grid-cols-2 gap-1 text-[10px] text-zinc-500">
                <div>Consumer Key: {status?.consumer_key_present ? "✅" : "❌"}</div>
                <div>Consumer Secret: {status?.consumer_secret_present ? "✅" : "❌"}</div>
                <div>Access Token: {status?.access_token_present ? "✅" : "❌"}</div>
                <div>Access Token Secret: {status?.access_token_secret_present ? "✅" : "❌"}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings */}
      <div className="rounded-xl border border-white/10 bg-[#0f141b] p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-white">Automatisches Posten</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">Wenn aktiv, wird bei jedem Publish ein Tweet ausgeloest.</div>
          </div>
          <button
            type="button"
            onClick={() => setAutoPost((v) => !v)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium ${autoPost ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-700/40 text-zinc-400"}`}
            data-testid="twitter-toggle-autopost"
          >
            {autoPost ? <><Zap className="mr-1 inline h-3.5 w-3.5" />Aktiv</> : <><ZapOff className="mr-1 inline h-3.5 w-3.5" />Deaktiviert</>}
          </button>
        </div>

        <div>
          <label className="text-xs text-zinc-400">Tweet-Template ({"{title}"}, {"{url}"}, {"{excerpt}"})</label>
          <Textarea
            value={tmpl}
            onChange={(e) => setTmpl(e.target.value)}
            rows={4}
            className="mt-1 bg-[#10141b] border-white/10 text-zinc-200 font-mono text-xs"
            data-testid="twitter-template"
          />
        </div>

        <div className="flex gap-2">
          <Button type="button" onClick={onSaveSettings} disabled={busy} className="btn-primary rounded-lg px-3 py-2 text-xs" data-testid="twitter-save-settings">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Speichern"}
          </Button>
        </div>
      </div>

      {/* Test Tweet */}
      {status?.connected && (
        <div className="rounded-xl border border-white/10 bg-[#0f141b] p-4 space-y-2">
          <div className="text-sm font-semibold text-white">Test-Tweet senden</div>
          <p className="text-[11px] text-zinc-500">
            Falls hier &bdquo;Forbidden (403)&ldquo; auftritt: in Twitter-Developer-App unter
            &bdquo;User authentication settings&ldquo; App-Permission auf <strong>Read and write</strong> stellen
            und Access Token neu generieren.
          </p>
          <Input
            value={testText}
            onChange={(e) => setTestText(e.target.value)}
            className="bg-[#10141b] border-white/10 text-zinc-200"
            data-testid="twitter-test-text"
          />
          <Button type="button" onClick={onTestTweet} disabled={busy || !testText.trim()} className="btn-primary rounded-lg px-3 py-2 text-xs" data-testid="twitter-test-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1 h-4 w-4" /> Test-Tweet senden</>}
          </Button>
        </div>
      )}
    </div>
  );
};

export default TwitterPanel;
